// ============================================================================
// RHFormsEntrevistaDesligamento_Historico.js
// Aba "Histórico" da Entrevista de Desligamento — série do formato ANTIGO
// (pré-reformulação): média 1-5 por pergunta das respostas legadas, como no BI
// MCDES_16. Lê GET /api/forms-rh/desligamento/historico (agregado, anônimo).
// Filtro por loja + período (Ano/Mês) e mostra o PERÍODO dos dados disponíveis.
// Série separada do relatório novo (as perguntas mudaram).
// ============================================================================

import { useEffect, useState, useCallback } from 'react';
import Sidebar from '../../Sidebar';
import { Filter, Archive } from 'lucide-react';

const MESES = [
  { v: '1', nome: 'Janeiro' }, { v: '2', nome: 'Fevereiro' }, { v: '3', nome: 'Março' },
  { v: '4', nome: 'Abril' }, { v: '5', nome: 'Maio' }, { v: '6', nome: 'Junho' },
  { v: '7', nome: 'Julho' }, { v: '8', nome: 'Agosto' }, { v: '9', nome: 'Setembro' },
  { v: '10', nome: 'Outubro' }, { v: '11', nome: 'Novembro' }, { v: '12', nome: 'Dezembro' },
];

// Régua de cor da escala 1-5: verde ≥4, âmbar ≥3, vermelho <3.
const corNota = (v) => (v == null ? '#94a3b8' : v >= 4 ? '#10b981' : v >= 3 ? '#f59e0b' : '#ef4444');

// ISO → 'MM/AAAA'. Usa os componentes UTC (a DATA vem como meia-noite; evita o dia "andar" no fuso).
const fmtMesAno = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${String(d.getUTCMonth() + 1).padStart(2, '0')}/${d.getUTCFullYear()}`;
};
const anoDe = (iso) => (iso ? new Date(iso).getUTCFullYear() : null);

function EntrevistaDesligamentoHistorico({ embedded = false }) {
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
  const getMarginLeft = () => {
    if (window.innerWidth <= 480) return isCollapsed ? '60px' : '0px';
    return isCollapsed ? '100px' : '260px';
  };
  useEffect(() => {
    const onResize = () => { if (window.innerWidth <= 768) setIsCollapsed(true); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [dados, setDados] = useState({ n: 0, periodo: null, periodoTotal: null, lojas: [], perguntas: [] });
  const [lojas, setLojas] = useState([]);
  const [anos, setAnos] = useState([]);
  const [periodoTotal, setPeriodoTotal] = useState(null); // faixa total (fixa, p/ o texto informativo)
  const [filterLoja, setFilterLoja] = useState('');
  const [filterAno, setFilterAno] = useState('');
  const [filterMes, setFilterMes] = useState('');
  const [carregando, setCarregando] = useState(false);

  const buscar = useCallback(async ({ loja = '', inicio = '', fim = '', primeiraCarga = false } = {}) => {
    setCarregando(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Token não encontrado. Faça o login novamente.');
      const qs = new URLSearchParams();
      if (loja) qs.set('loja', loja);
      if (inicio) qs.set('inicio', inicio);
      if (fim) qs.set('fim', fim);
      const url = `/api/forms-rh/desligamento/historico${qs.toString() ? `?${qs}` : ''}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        let msg = `Status: ${res.status} ${res.statusText}`;
        try { const j = await res.json(); msg = j.message || JSON.stringify(j); } catch (e) { /* sem JSON */ }
        throw new Error(msg);
      }
      const data = await res.json();
      setDados(data);
      if (primeiraCarga) {
        setLojas(data.lojas || []);
        setPeriodoTotal(data.periodoTotal || null);
        if (data.periodoTotal) {
          const a1 = anoDe(data.periodoTotal.inicio);
          const a2 = anoDe(data.periodoTotal.fim);
          const lista = [];
          for (let a = a2; a >= a1; a--) lista.push(String(a)); // do mais recente pro mais antigo
          setAnos(lista);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar histórico de desligamento:', error.message);
      alert(`Erro ao buscar histórico: ${error.message}`);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { buscar({ primeiraCarga: true }); }, [buscar]);

  const aplicarFiltros = () => {
    let inicio = ''; let fim = '';
    if (filterAno) {
      if (filterMes) {
        const mm = String(filterMes).padStart(2, '0');
        const ultimoDia = new Date(Number(filterAno), Number(filterMes), 0).getDate();
        inicio = `${filterAno}-${mm}-01`;
        fim = `${filterAno}-${mm}-${String(ultimoDia).padStart(2, '0')}`;
      } else {
        inicio = `${filterAno}-01-01`;
        fim = `${filterAno}-12-31`;
      }
    }
    buscar({ loja: filterLoja, inicio, fim });
  };

  const mediasValidas = dados.perguntas.map((p) => p.media).filter((v) => v != null);
  const mediaGeral = mediasValidas.length ? mediasValidas.reduce((a, v) => a + v, 0) / mediasValidas.length : null;

  return (
    <div style={embedded ? { width: '100%' } : { display: 'flex', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {!embedded && <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />}
      <div style={{ flex: 1, marginLeft: embedded ? 0 : getMarginLeft(), transition: 'margin-left 0.3s ease' }}>
        {!embedded && (
          <div style={{ height: '80px', backgroundColor: '#fff', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', padding: '0 40px', position: 'sticky', top: 0, zIndex: 10 }}>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#0f172a' }}>Histórico — Desligamento</h2>
          </div>
        )}

        <div style={{ padding: '40px', maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ marginBottom: '20px' }}>
            <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#0f172a', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Archive size={26} color="#e11d48" /> Histórico (pré-reformulação)
            </h1>
            <p style={{ color: '#64748b', fontSize: '15px', margin: 0 }}>
              Média por pergunta das entrevistas no formato antigo (escala 1-5), como no BI MCDES_16. Série separada das notas atuais (as perguntas mudaram).
            </p>
          </div>

          {/* Info do período dos dados */}
          <div style={{ backgroundColor: '#fff1f2', border: '1px solid #fecdd3', borderRadius: '12px', padding: '14px 18px', marginBottom: '20px', fontSize: '14px', color: '#9f1239' }}>
            {periodoTotal
              ? <>Dados históricos disponíveis de <strong>{fmtMesAno(periodoTotal.inicio)}</strong> a <strong>{fmtMesAno(periodoTotal.fim)}</strong>. Exibindo <strong>{dados.n}</strong> resposta{dados.n === 1 ? '' : 's'}{dados.periodo ? <> ({fmtMesAno(dados.periodo.inicio)}–{fmtMesAno(dados.periodo.fim)})</> : null}.</>
              : 'Sem dados históricos.'}
          </div>

          {/* FILTROS */}
          <div style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '24px', alignItems: 'flex-end' }}>
            <div style={{ flex: '1 1 180px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Loja</label>
              <select value={filterLoja} onChange={(e) => setFilterLoja(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }}>
                <option value="">Todas</option>
                {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div style={{ flex: '1 1 110px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Ano</label>
              <select value={filterAno} onChange={(e) => setFilterAno(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }}>
                <option value="">Todos</option>
                {anos.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div style={{ flex: '1 1 140px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Mês</label>
              <select value={filterMes} onChange={(e) => setFilterMes(e.target.value)} disabled={!filterAno} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', backgroundColor: filterAno ? '#fff' : '#f1f5f9' }}>
                <option value="">Todos</option>
                {MESES.map((m) => <option key={m.v} value={m.v}>{m.nome}</option>)}
              </select>
            </div>
            <button onClick={aplicarFiltros} disabled={carregando} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#0f172a', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', opacity: carregando ? 0.6 : 1 }}>
              <Filter size={16} /> Aplicar Filtros
            </button>
          </div>

          {/* Média por pergunta */}
          <div style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>Média por pergunta (1-5)</h3>
              <span style={{ fontSize: '14px', color: '#64748b' }}>
                Média geral: <strong style={{ color: corNota(mediaGeral), fontVariantNumeric: 'tabular-nums' }}>{mediaGeral == null ? '—' : mediaGeral.toFixed(2)}</strong>
              </span>
            </div>
            {dados.n === 0 ? (
              <p style={{ color: '#94a3b8', fontSize: '14px', margin: 0 }}>Sem respostas antigas no período/loja selecionados.</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '10px' }}>
                {dados.perguntas.map((p) => (
                  <div key={p.label} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '13px', color: '#475569', border: '1px solid #f1f5f9', borderRadius: '10px', padding: '12px 14px' }}>
                    <span style={{ flex: 1 }}>{p.label}</span>
                    <span style={{ fontWeight: 800, color: corNota(p.media), fontVariantNumeric: 'tabular-nums' }}>{p.media == null ? '—' : p.media.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

export default EntrevistaDesligamentoHistorico;
